import { sha256 } from '@noble/hashes/sha256';
import { bytesToHex } from '@noble/hashes/utils';

export type TemplateReviewSeverity = 'info' | 'warning' | 'error';
export type TemplateReviewCategory = 'security' | 'cost' | 'validation';

export interface TemplateReviewFinding {
  id: string;
  severity: TemplateReviewSeverity;
  category: TemplateReviewCategory;
  message: string;
  file?: string;
}

export interface ReviewableTemplateDraft {
  packName: string;
  estimatedMonthlyCost?: number;
  files: Array<{ path: string; content: string }>;
}

export interface TemplateReviewReport {
  version: 1;
  status: 'passed' | 'failed';
  generatedAt: string;
  draftDigest: string;
  estimatedMonthlyCost?: number;
  findings: TemplateReviewFinding[];
  evidence: {
    fileCount: number;
    scannedBytes: number;
    securityRules: string[];
  };
}

const securityRules = [
  'credential-material',
  'public-ingress',
  'wildcard-administration',
  'destructive-command',
];

function encodeUtf8(value: string): Uint8Array {
  const bytes: number[] = [];
  for (const symbol of value) {
    const code = symbol.codePointAt(0)!;
    if (code <= 0x7f) bytes.push(code);
    else if (code <= 0x7ff) {
      bytes.push(0xc0 | (code >> 6), 0x80 | (code & 0x3f));
    } else if (code <= 0xffff) {
      bytes.push(
        0xe0 | (code >> 12),
        0x80 | ((code >> 6) & 0x3f),
        0x80 | (code & 0x3f),
      );
    } else {
      bytes.push(
        0xf0 | (code >> 18),
        0x80 | ((code >> 12) & 0x3f),
        0x80 | ((code >> 6) & 0x3f),
        0x80 | (code & 0x3f),
      );
    }
  }
  return Uint8Array.from(bytes);
}

/** Produces deterministic review evidence; publication must recompute it server-side. */
export function reviewTemplateDraft(
  draft: ReviewableTemplateDraft,
): TemplateReviewReport {
  const files = [...draft.files].sort((left, right) =>
    left.path.localeCompare(right.path),
  );
  let digestInput = draft.packName;
  const findings: TemplateReviewFinding[] = [];
  let scannedBytes = 0;

  if (
    draft.estimatedMonthlyCost === undefined ||
    !Number.isFinite(draft.estimatedMonthlyCost) ||
    draft.estimatedMonthlyCost < 0
  ) {
    findings.push({
      id: 'cost-estimate-required',
      severity: 'error',
      category: 'cost',
      message: 'A reviewed non-negative monthly cost estimate is required.',
    });
  } else {
    findings.push({
      id: 'cost-estimate-recorded',
      severity: 'info',
      category: 'cost',
      message: `Estimated monthly cost: ${draft.estimatedMonthlyCost}.`,
    });
  }

  if (!files.some(file => file.path === 'template.yaml')) {
    findings.push({
      id: 'template-missing',
      severity: 'error',
      category: 'validation',
      message: 'template.yaml is required.',
    });
  }

  for (const file of files) {
    digestInput += `\0${file.path}\0${file.content}`;
    scannedBytes += Buffer.byteLength(file.content, 'utf8');
    const rules: Array<[string, RegExp, string]> = [
      [
        'credential-material',
        /-----BEGIN (?:RSA |EC |OPENSSH )?PRIVATE KEY-----|\b(?:AKIA|ASIA)[A-Z0-9]{16}\b|(?:client_secret|password)\s*[:=]\s*["'][^${][^"']{7,}["']/i,
        'Possible credential material is embedded in the draft.',
      ],
      [
        'public-ingress',
        /(?:cidr(?:_blocks)?|source_address_prefix|sourceAddressPrefix)\s*[:=]\s*(?:\[\s*)?["']?0\.0\.0\.0\/0/i,
        'Public ingress from 0.0.0.0/0 requires explicit security review.',
      ],
      [
        'wildcard-administration',
        /(?:Action|actions?|permissions?)\s*["']?\s*[:=]\s*(?:\[\s*)?["']?\*(?:["']|\s|,|\])/i,
        'Wildcard administrative permissions are not accepted.',
      ],
      [
        'destructive-command',
        /\brm\s+-rf\s+\/\b|\baz\s+(?:group|account)\s+delete\b|\baws\s+organizations\s+delete-/i,
        'A broad destructive command was detected.',
      ],
    ];
    for (const [id, pattern, message] of rules) {
      if (pattern.test(file.content)) {
        findings.push({
          id,
          severity: 'error',
          category: 'security',
          message,
          file: file.path,
        });
      }
    }
  }

  findings.push({
    id: 'draft-structure-validated',
    severity: 'info',
    category: 'validation',
    message: `${files.length} files were included in the reviewed draft.`,
  });

  return {
    version: 1,
    status: findings.some(finding => finding.severity === 'error')
      ? 'failed'
      : 'passed',
    generatedAt: new Date().toISOString(),
    draftDigest: bytesToHex(sha256(encodeUtf8(digestInput))),
    estimatedMonthlyCost: draft.estimatedMonthlyCost,
    findings,
    evidence: { fileCount: files.length, scannedBytes, securityRules },
  };
}
